import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { FC, useState } from 'react';

import bottomDecor from 'media/wrapped/bottom-decor.svg';
import centerDecor from 'media/wrapped/center-decor.svg';
import leftDecor from 'media/wrapped/left-decor.svg';
import topDecor from 'media/wrapped/top-decor.svg';

import { formatCurrencyToShort, padNumber } from 'utils/format-helper';

interface ImageCarouselProps {
  className?: string;
  wrappedData: any[];
  syndicateName?: string | null;
}

const ImageCarousel: FC<ImageCarouselProps> = ({ className = '', wrappedData, syndicateName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex(prev => (prev - 1 + wrappedData.length) % wrappedData.length);
  };

  const goToNext = () => {
    setCurrentIndex(prev => (prev + 1) % wrappedData.length);
  };

  const goToImage = (index: number) => {
    setCurrentIndex(index);
  };

  if (wrappedData.length === 0) return null;

  return (
    <div className={`relative group ${className} overflow-hidden`}>
      <div className="relative bg-white rounded-2xl p-1 shadow-2xl transform transition-transform duration-300 group-hover:scale-[1.02] overflow-hidden">
        <div
          className={`relative overflow-hidden rounded-xl h-96 w-72 md:h-112 md:w-96 lg:h-[480px] lg:w-[440px] bg-[#161515]`}
        >
          {/* DECORATIONS */}
          <div>
            <img
              src={topDecor}
              alt="top-decor"
              className="absolute top-0 right-0 h-24 w-24 opacity-50"
            />
            <img
              src={bottomDecor}
              alt="bottom-decor"
              className="absolute bottom-0 right-0 h-24 w-24"
            />
            <img
              src={centerDecor}
              alt="center-decor"
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-64 w-64 opacity-30"
            />
            <img
              src={leftDecor}
              alt="left-decor"
              className="absolute top-1/3 -left-40 transform -translate-y-1/2 h-64 w-64 opacity-10"
            />
            {/* <img src={logoWhite} alt="logo-white" className="absolute -bottom-4 left-10 h-40 w-40 opacity-30" /> */}
            <span className="absolute bottom-12 pb-1 left-10 text-white font-bold text-sm">
              {syndicateName}
            </span>
          </div>
          {/* DECORATIONS */}
          <div className="flex justify-between flex-col h-full p-10">
            <h2
              style={{
                color: `#C9A876`,
              }}
              className="text-2xl uppercase font-black mb-3 drop-shadow-lg text-left tracking-wider"
            >
              {wrappedData[currentIndex].title}
            </h2>
            <div className="relative z-10 space-y-3 font-bold text-white text-center tracking-tight">
              {wrappedData[currentIndex].format === 'ARRAY' ? (
                <div className="text-3xl">
                  <ul className="list-decimal list-inside">
                    {wrappedData[currentIndex].value.map((val: any, idx: number) => (
                      <li key={idx}>{val}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {wrappedData[currentIndex].format === 'CURRENCY' ? (
                <p className="text-7xl">
                  {formatCurrencyToShort(wrappedData[currentIndex].value)}{' '}
                </p>
              ) : null}
              {wrappedData[currentIndex].format === 'NUMBER' ? (
                <p className="text-7xl">{padNumber(wrappedData[currentIndex].value)} </p>
              ) : null}
              {wrappedData[currentIndex].format === 'SMALL_STRING' ? (
                <p className="text-3xl">{wrappedData[currentIndex].value} </p>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-grow h-0.5 bg-gradient-to-r from-white/40 to-transparent rounded-full"></div>
              <span className="text-xs text-white font-medium">2025</span>
            </div>
          </div>

          {wrappedData.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 z-10"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-6 h-6 text-gray-800" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 z-10"
                aria-label="Next image"
              >
                <ChevronRight className="w-6 h-6 text-gray-800" />
              </button>
            </>
          )}

          {wrappedData.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {wrappedData.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToImage(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    index === currentIndex ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/75'
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageCarousel;
